'use client'

import { useState, useCallback, useEffect } from 'react'

export type ScriptDisplayMode = 'both' | 'simplified' | 'traditional'
export type PronunciationDisplayMode = 'both' | 'pinyin' | 'zhuyin'
export type ScriptPriority = 'simplified' | 'traditional'
export type PronunciationPriority = 'pinyin' | 'zhuyin'
export type ChineseFontStyle =
  | 'system'
  | 'lxgw-wenkai-tc'
  | 'noto-sans-tc'
  | 'noto-sans-sc'

// 'auto' = use UI locale, or specific locale code for override
export type DefinitionLocale = 'auto' | 'en' | 'es'

type Settings = {
  scriptDisplay: ScriptDisplayMode
  scriptPriority: ScriptPriority
  pronunciationDisplay: PronunciationDisplayMode
  pronunciationPriority: PronunciationPriority
  chineseFont: ChineseFontStyle
  definitionLocale: DefinitionLocale
  flashcardAutoplayAudio: boolean
}

const defaultSettings: Settings = {
  scriptDisplay: 'both',
  scriptPriority: 'simplified',
  pronunciationDisplay: 'both',
  pronunciationPriority: 'pinyin',
  chineseFont: 'system',
  definitionLocale: 'auto',
  flashcardAutoplayAudio: true
}

const STORAGE_KEY = 'ch-dict-settings'

function loadSettings(): Settings {
  if (typeof window === 'undefined') {
    return defaultSettings
  }
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      return { ...defaultSettings, ...JSON.parse(stored) }
    }
  } catch (error) {
    console.error('Failed to load settings:', error)
  }
  return defaultSettings
}

function saveSettings(settings: Settings) {
  if (typeof window === 'undefined') {
    return
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
  } catch (error) {
    console.error('Failed to save settings:', error)
  }
}

export function useSettings() {
  // Start with defaults to match server render, then hydrate from localStorage
  const [settings, setSettingsState] = useState<Settings>(defaultSettings)
  const [isHydrated, setIsHydrated] = useState(false)

  // Load settings from localStorage after mount (client-side only)
  useEffect(() => {
    const id = window.setTimeout(() => {
      setSettingsState(loadSettings())
      setIsHydrated(true)
    }, 0)

    return () => window.clearTimeout(id)
  }, [])

  const setSettings = useCallback((updates: Partial<Settings>) => {
    setSettingsState((prev) => {
      const newSettings = { ...prev, ...updates }
      saveSettings(newSettings)
      return newSettings
    })
  }, [])

  return {
    settings,
    isHydrated,
    setSettings,
    setScriptDisplay: (mode: ScriptDisplayMode) =>
      setSettings({ scriptDisplay: mode }),
    setScriptPriority: (priority: ScriptPriority) =>
      setSettings({ scriptPriority: priority }),
    setPronunciationDisplay: (mode: PronunciationDisplayMode) =>
      setSettings({ pronunciationDisplay: mode }),
    setPronunciationPriority: (priority: PronunciationPriority) =>
      setSettings({ pronunciationPriority: priority }),
    setChineseFont: (font: ChineseFontStyle) =>
      setSettings({ chineseFont: font }),
    setDefinitionLocale: (locale: DefinitionLocale) =>
      setSettings({ definitionLocale: locale }),
    setFlashcardAutoplayAudio: (enabled: boolean) =>
      setSettings({ flashcardAutoplayAudio: enabled })
  }
}
